import { useState } from 'react';
import { Mail, Send } from 'lucide-react';
import { GradientTitle } from '../components/ui';
import { Helmet } from 'react-helmet-async';
import { Input } from '../components/ui';
import { useContactForm } from '../hooks/useApi';

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const { mutate: submitForm, isPending, isSuccess, isError } = useContactForm();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    submitForm(formData, {
      onSuccess: () => {
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    });
  };

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>Contact Us | Cognitive Bias Lab</title>
        <meta name="description" content="Have questions or feedback about cognitive biases? Get in touch with our team at Cognitive Bias Lab. We welcome your inquiries and suggestions." />
        <link rel="canonical" href="https://www.cognitivebiaslab.com/contact/" key="canonical" data-source="page-component" />
        
        {/* OpenGraph tags for better social sharing */}
        <meta property="og:title" content="Contact Us | Cognitive Bias Lab" />
        <meta 
          property="og:description" 
          content="Have questions or feedback about cognitive biases? Get in touch with our team at Cognitive Bias Lab. We welcome your inquiries and suggestions."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cognitivebiaslab.com/contact/" />
        <meta property="og:site_name" content="Cognitive Bias Lab" />
        <meta property="og:image" content="https://www.cognitivebiaslab.com/logo.webp" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:type" content="image/webp" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact Us | Cognitive Bias Lab" />
        <meta 
          name="twitter:description" 
          content="Have questions or feedback about cognitive biases? Get in touch with our team at Cognitive Bias Lab. We welcome your inquiries and suggestions."
        />
        <meta name="twitter:image" content="https://www.cognitivebiaslab.com/logo.webp" />
      </Helmet>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-2xl mx-auto">
          <GradientTitle className="text-3xl md:text-4xl mb-4 text-center" useH1={true}>
            Contact Us
          </GradientTitle>
          
          <p className="text-gray-600 text-lg text-center mb-8 max-w-lg mx-auto">
            Have questions, suggestions, or feedback about our website? We'd love to hear from you!
          </p>

          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-gray-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <Input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <Input
                  type="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                  Subject
                </label>
                <Input
                  type="text"
                  id="subject"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <Input
                  as="textarea"
                  id="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className="resize-none"
                />
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isPending}
                  className={`group bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-3 rounded-xl 
                    text-white hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl 
                    relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed w-48`}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {isPending ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                        Send Message
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-orange-500 
                    opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
              </div>

              {isSuccess && (
                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 rounded-xl border border-green-100 shadow-sm">
                  Thank you for your message! We'll get back to you soon.
                </div>
              )}

              {isError && (
                <div className="p-4 bg-gradient-to-r from-red-50 to-rose-50 text-red-700 rounded-xl border border-red-100 shadow-sm">
                  Sorry, something went wrong. Please try again later.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
}; 